import { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Collapse, TextField, Divider } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import AuthContext from '../../context';
import { get, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    buttonContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 5
    },
    updateBtn: {
        width: '100%',
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none'
        }
    }
}));

const defCarrierCatalog = { id: 0, name: 'Choose a carrier' };

const CarrierDetailEdit = ({ open, id, carrier, setCarrier, permissions, onClose }) => {

    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const isMounted = useRef(false);
    const [state, setState] = useState({});
    const [carriers, setCarriers] = useState([defCarrierCatalog]);

    useEffect(() => {
        if (open && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [open]);

    useEffect(() => {
        setState(carrier);
    }, [carrier]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketCarrier/create');
        if (response.status === 200) {
            setCarriers(response.carriers);
        } else {
            console.log('[TicketCarrierCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, [target.name]: target.value }));

    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, [key]: value }));

    const validate = () => {
        if (!permissions.update_attr) {
            showToast('You have no permissions for this action', 'error', 6500);
            return false;
        }

        if (JSON.stringify(state) === JSON.stringify(carrier)) {
            showToast('Update some field', 'error');
            return false;
        }

        if (!state?.date || Number(state?.amount) <= 0 || Number(state?.carrier_catalog?.id) < 1) {
            showToast('All fields are required', 'error');
            return false;
        }

        const cond = window.confirm('Are you sure you want to update this carrier?');
        return cond;
    }

    const update = async () => {
        const cond = validate();
        if (cond) {
            showLoader(true);
            const response = await put(`/ticketCarrier/${id}/update`, state);
            if (response.status === 200) {
                setCarrier(response.data);
                showToast(response.message);
                onClose();
            } else {
                console.log('[CarrierDetailEdit] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const clear = () => setState(carrier);

    return (
        <Collapse in={open}>
            <Divider />
            <h3>{'Edit carrier'}</h3>
            <Grid container>
                <Grid item xs={6} md={3} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='date'
                                placeholder='Date'
                                name='date'
                                value={state?.date ?? ''}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Amount'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='number'
                                placeholder='Amount'
                                name='amount'
                                value={state?.amount ?? 0}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Container'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Container'
                                name='container'
                                value={state?.container ?? ''}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Carrier'}</FormLabel>
                        <Autocomplete
                            options={carriers}
                            value={state?.carrier_catalog ?? defCarrierCatalog}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleAutocomplete('carrier_catalog', value)}
                            renderInput={params => <TextField {...params} placeholder='Carrier' />}
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={6} md={2} xl={1} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        color='secondary'
                        style={{ width: '100%' }}
                        onClick={clear}
                    >
                        {'Cancel'}
                    </Button>
                </Grid>
                {
                    (permissions.update_attr) &&
                    <Grid item xs={6} md={2} xl={1} className={classes.buttonContainer}>
                        <Button
                            variant='contained'
                            className={classes.updateBtn}
                            onClick={update}
                        >
                            {'Update'}
                        </Button>
                    </Grid>
                }
            </Grid>
        </Collapse>
    );
}

export default CarrierDetailEdit;